import React, { useState, useMemo } from 'react';
import { Activity, Timer, Zap, Map as MapIcon, Waves, Heart, ListOrdered, Settings2, ArrowRightLeft, Menu, X, Upload, Share2, Trash2, RotateCcw, AlertCircle } from 'lucide-react';
import { UploadZone } from './components/UploadZone';
import { MapVisualization } from './components/MapVisualization';
import { SpeedChart } from './components/SpeedChart';
import { parseGPXXml, analyzeTrack, calculateSegmentStats } from './utils/gpxParser';
import { GPXData, AnalysisResult } from './types';

const App: React.FC = () => {
  // Raw Data
  const [rawGpxData, setRawGpxData] = useState<GPXData | null>(null);
  
  // Analysis Settings
  const [speedThreshold, setSpeedThreshold] = useState<number>(6.0);
  const [minDuration, setMinDuration] = useState<number>(15.0);
  
  // Interactive State
  const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null);
  const [hiddenSegmentIds, setHiddenSegmentIds] = useState<number[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Trimming State: { [segmentId]: { start: pointsToRemoveFromStart, end: pointsToRemoveFromEnd } }
  const [segmentTrims, setSegmentTrims] = useState<Record<number, { start: number; end: number }>>({});

  const handleFileLoaded = (content: string, fileName: string) => {
    try {
      const raw = parseGPXXml(content, fileName);
      setRawGpxData(raw);
      setHiddenSegmentIds([]); 
      setSegmentTrims({}); // Reset trims on new file
      setSelectedSegmentId(null);
      setIsMenuOpen(false);
    } catch (error) {
      console.error("Failed to parse GPX", error);
      alert("Invalid GPX file or format not supported.");
    }
  };

  // Re-run analysis whenever thresholds or raw data changes
  const analysisData: AnalysisResult | null = useMemo(() => {
    if (!rawGpxData) return null;
    const baseAnalysis = analyzeTrack(rawGpxData.trackPoints, speedThreshold, minDuration);

    // Apply manual trims to the extracted segments
    const trimmedSegments = baseAnalysis.segments.map(seg => {
      const trim = segmentTrims[seg.id];
      if (!trim) return seg;

      // Calculate valid slice indices
      const startIdx = trim.start;
      const endIdx = seg.points.length - trim.end;

      // If we trimmed everything, return empty points (will be filtered or shown as 0)
      if (startIdx >= endIdx) {
        return { ...seg, points: [], stats: calculateSegmentStats([]) };
      }

      const trimmedPoints = seg.points.slice(startIdx, endIdx);
      const newStats = calculateSegmentStats(trimmedPoints);

      return { ...seg, points: trimmedPoints, stats: newStats };
    });

    return {
      ...baseAnalysis,
      segments: trimmedSegments
    };
  }, [rawGpxData, speedThreshold, minDuration, segmentTrims]);

  // Filter out hidden segments
  const visibleSegments = useMemo(() => {
    if (!analysisData) return [];
    return analysisData.segments.filter(s => !hiddenSegmentIds.includes(s.id) && s.points.length > 1);
  }, [analysisData, hiddenSegmentIds]);

  // Derive the currently selected segment object
  const selectedSegment = useMemo(() => {
    if (!visibleSegments || selectedSegmentId === null) return null;
    return visibleSegments.find(s => s.id === selectedSegmentId) || null;
  }, [visibleSegments, selectedSegmentId]);

  // Calculated Combined Stats for visible segments only
  const combinedStats = useMemo(() => {
    if (!visibleSegments.length) return null;
    const totalDist = visibleSegments.reduce((acc, s) => acc + s.stats.distance, 0);
    const totalDur = visibleSegments.reduce((acc, s) => acc + s.stats.duration, 0);
    // Weighted average speed
    const weightedSpeed = totalDur > 0 
      ? visibleSegments.reduce((acc, s) => acc + (s.stats.avgSpeed * s.stats.duration), 0) / totalDur
      : 0;
      
    return {
      distance: totalDist,
      duration: totalDur,
      avgSpeed: weightedSpeed,
      count: visibleSegments.length
    };
  }, [visibleSegments]);

  // Helper to format seconds into mm:ss
  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleCloseFile = () => {
    setRawGpxData(null);
    setSelectedSegmentId(null);
    setHiddenSegmentIds([]);
    setSegmentTrims({});
    setIsMenuOpen(false);
  };

  const handleShareApp = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'FoilFlow',
          text: 'Analyze your pump foil sessions with FoilFlow!',
          url: window.location.href,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleHideSegment = (id: number) => {
    setHiddenSegmentIds(prev => [...prev, id]);
    if (selectedSegmentId === id) {
      setSelectedSegmentId(null);
    }
  };

  const handleTrimStart = (id: number) => {
    setSegmentTrims(prev => {
      const current = prev[id] || { start: 0, end: 0 };
      return { ...prev, [id]: { ...current, start: current.start + 1 } };
    });
  };

  const handleTrimEnd = (id: number) => {
    setSegmentTrims(prev => {
      const current = prev[id] || { start: 0, end: 0 };
      return { ...prev, [id]: { ...current, end: current.end + 1 } };
    });
  };
  
  const handleResetTrim = (id: number) => {
    setSegmentTrims(prev => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-20 font-sans text-slate-200">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center space-x-3 group focus:outline-none"
              aria-label="Open Settings Menu"
            >
              <div className="p-2 rounded-lg bg-slate-900 group-hover:bg-slate-800 border border-slate-800 transition-colors">
                <Menu className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="flex items-center space-x-2">
                <Waves className="w-5 h-5 text-cyan-400" />
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                  FoilFlow
                </span>
              </div>
            </button>
          </div>

          {/* Right / Center: Quick Stats (Moved here) */}
          {combinedStats && (
             <div className="flex items-center space-x-4 md:space-x-6 text-sm">
                <div className="flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-800/50">
                   <ListOrdered className="w-4 h-4 text-blue-400" />
                   <span className="font-mono font-bold text-white">{visibleSegments.length}</span>
                   <span className="hidden sm:inline text-slate-500 text-xs uppercase">Runs</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-800/50">
                   <MapIcon className="w-4 h-4 text-emerald-400" />
                   <span className="font-mono font-bold text-white">{(combinedStats.distance).toFixed(2)}</span>
                   <span className="text-slate-500 text-xs">km</span>
                </div>
                <div className="hidden sm:flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-800/50">
                   <Timer className="w-4 h-4 text-amber-400" />
                   <span className="font-mono font-bold text-white">{formatDuration(combinedStats.duration)}</span>
                </div>
             </div>
          )}
        </div>
      </header>

      {/* Settings Drawer / Menu */}
      {isMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[60]" 
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="fixed top-0 left-0 h-full w-80 bg-slate-900 border-r border-slate-800 z-[70] shadow-2xl transform transition-transform duration-300 ease-out flex flex-col">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center">
               <h2 className="text-lg font-bold text-white flex items-center gap-2">
                 <Settings2 className="w-5 h-5 text-cyan-400" />
                 Settings
               </h2>
               <button onClick={() => setIsMenuOpen(false)} className="text-slate-400 hover:text-white">
                 <X className="w-6 h-6" />
               </button>
            </div>
            
            <div className="p-6 space-y-8 flex-1 overflow-y-auto">
               {/* Analysis Config */}
               <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2 flex justify-between">
                      <span>Min Speed Threshold</span>
                      <span className="text-cyan-400 font-mono">{speedThreshold} km/h</span>
                    </label>
                    <input 
                      type="range" 
                      min="3" 
                      max="15" 
                      step="0.5" 
                      value={speedThreshold}
                      onChange={(e) => setSpeedThreshold(parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <p className="text-xs text-slate-500 mt-2">Adjust this to filter out paddling or waiting periods.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2 flex justify-between">
                      <span>Min Duration</span>
                      <span className="text-cyan-400 font-mono">{minDuration} sec</span>
                    </label>
                    <input 
                      type="range" 
                      min="1" 
                      max="30" 
                      step="1" 
                      value={minDuration}
                      onChange={(e) => setMinDuration(parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <p className="text-xs text-slate-500 mt-2">Filter out short bursts or false detections.</p>
                  </div>
               </div>

               {/* Actions */}
               <div className="pt-6 border-t border-slate-800 space-y-4">
                 {hiddenSegmentIds.length > 0 && (
                    <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-300">{hiddenSegmentIds.length} runs hidden</span>
                        <button 
                          onClick={() => setHiddenSegmentIds([])}
                          className="text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 px-2 py-1 rounded-md flex items-center gap-1 transition-colors"
                        >
                          <RotateCcw className="w-3 h-3" /> Restore
                        </button>
                      </div>
                    </div>
                 )}

                 <button 
                    onClick={handleShareApp}
                    className="w-full flex items-center justify-center space-x-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 py-3 rounded-xl border border-indigo-500/30 transition-all"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share App</span>
                  </button>

                 {rawGpxData && (
                    <button 
                      onClick={handleCloseFile}
                      className="w-full flex items-center justify-center space-x-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 py-3 rounded-xl border border-red-500/30 transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload New File</span>
                    </button>
                 )}
               </div>
            </div>
            
            <div className="p-4 border-t border-slate-800 text-xs text-center text-slate-600">
               FoilFlow v1.11
            </div>
          </div>
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        {!analysisData || !rawGpxData ? (
          <div className="flex flex-col items-center justify-center min-h-[70vh] animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                Unlock Your <span className="text-cyan-400">Flow</span>
              </h1>
              <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                Advanced GPX analysis for Pump Foil and Wing Foil. 
                Upload your track to visualize segments, analyze speed, and track your progress.
              </p>
            </div>
            <UploadZone onFileLoaded={handleFileLoaded} />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 w-full max-w-5xl">
               <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-cyan-500/30 transition-colors">
                 <Activity className="w-10 h-10 text-blue-500 mb-4" />
                 <h3 className="text-lg font-bold text-white mb-2">Smart Detection</h3>
                 <p className="text-slate-400 text-sm">Automatically isolates foiling runs from paddling and resting periods.</p>
               </div>
               <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-cyan-500/30 transition-colors">
                 <MapIcon className="w-10 h-10 text-emerald-500 mb-4" />
                 <h3 className="text-lg font-bold text-white mb-2">Interactive Map</h3>
                 <p className="text-slate-400 text-sm">Explore your path with speed-based heatmaps on high-res satellite imagery.</p>
               </div>
               <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-cyan-500/30 transition-colors">
                 <ListOrdered className="w-10 h-10 text-yellow-500 mb-4" />
                 <h3 className="text-lg font-bold text-white mb-2">Detailed Breakdown</h3>
                 <p className="text-slate-400 text-sm">Analyze every single run with precise distance, time, and heart rate data.</p>
               </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in pb-12">
            
            {/* Error State: No Segments */}
            {visibleSegments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[60vh] text-center space-y-6 bg-slate-900/50 rounded-3xl border border-slate-800">
                   <div className="p-6 bg-slate-900 rounded-full border-2 border-slate-800 shadow-xl">
                     <AlertCircle className="w-12 h-12 text-amber-500" />
                   </div>
                   <div className="max-w-md space-y-3 px-4">
                     <h3 className="text-2xl font-bold text-white">No Runs Detected</h3>
                     <p className="text-slate-400 leading-relaxed">
                       {hiddenSegmentIds.length > 0 
                         ? "You have hidden all detected runs from your view." 
                         : `No segments matched the current settings (Min Speed: ${speedThreshold} km/h, Min Duration: ${minDuration}s).`}
                     </p>
                   </div>
                   <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xs px-4">
                     <button 
                       onClick={() => setIsMenuOpen(true)} 
                       className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-medium transition-all shadow-lg shadow-cyan-900/20"
                     >
                       Adjust Settings
                     </button>
                     <button 
                       onClick={handleCloseFile} 
                       className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-3 rounded-xl font-medium transition-all border border-slate-700"
                     >
                       Upload New File
                     </button>
                   </div>
                   {hiddenSegmentIds.length > 0 && (
                      <button 
                        onClick={() => setHiddenSegmentIds([])}
                        className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" /> Restore Hidden Runs
                      </button>
                   )}
                </div>
            ) : (
              <>
                {/* 1. Map Visualization (Hero) */}
                <div className="w-full h-[50vh] md:h-[500px] lg:h-[600px] bg-slate-900 rounded-2xl overflow-hidden shadow-xl border border-slate-800 relative z-0">
                   <MapVisualization 
                      trackPoints={rawGpxData.trackPoints} 
                      segments={visibleSegments} 
                      selectedSegmentId={selectedSegmentId}
                      onSelectSegment={setSelectedSegmentId}
                      minSpeed={speedThreshold}
                      maxSpeed={analysisData.stats.maxSpeed > speedThreshold ? analysisData.stats.maxSpeed : speedThreshold + 15}
                      hideBackground={selectedSegmentId === null} 
                      onTrimStart={handleTrimStart}
                      onTrimEnd={handleTrimEnd}
                      onTrimReset={handleResetTrim}
                    />
                </div>

                {/* 2. Chart (Full Width) */}
                <div className="w-full">
                     <SpeedChart 
                        data={rawGpxData.trackPoints} 
                        segments={visibleSegments}
                        selectedSegment={selectedSegment}
                      />
                </div>

                {/* 3. Segment Table (The List) */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                   <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
                      <h3 className="font-bold text-lg text-white flex items-center gap-2">
                        <ListOrdered className="w-5 h-5 text-cyan-400" />
                        Run Breakdown
                      </h3>
                      <span className="px-3 py-1 bg-slate-800 rounded-full text-xs font-mono text-slate-400">
                        {visibleSegments.length} segments
                      </span>
                   </div>
                   
                   <div className="overflow-x-auto">
                     <table className="w-full text-left border-collapse min-w-[600px]">
                       <thead>
                         <tr className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider border-b border-slate-800">
                           <th className="p-4 font-semibold">Run</th>
                           <th className="p-4 font-semibold">Start Time</th>
                           <th className="p-4 font-semibold text-right">Duration</th>
                           <th className="p-4 font-semibold text-right">Distance</th>
                           <th className="p-4 font-semibold text-right">Avg Speed</th>
                           <th className="p-4 font-semibold text-right">Max Speed</th>
                           <th className="p-4 font-semibold text-right">Avg HR</th>
                           <th className="p-4"></th>
                         </tr>
                       </thead>
                       <tbody className="text-sm divide-y divide-slate-800/50">
                          {/* Combined Stats Row */}
                          {combinedStats && (
                            <tr 
                              onClick={() => setSelectedSegmentId(null)}
                              className={`cursor-pointer transition-colors ${
                                selectedSegmentId === null 
                                  ? 'bg-cyan-900/20 hover:bg-cyan-900/30' 
                                  : 'bg-slate-900 hover:bg-slate-800'
                              }`}
                            >
                              <td className="p-4 font-bold text-cyan-400 flex items-center gap-2">
                                <ArrowRightLeft className="w-4 h-4" />
                                All Runs
                              </td>
                              <td className="p-4 text-slate-500 font-mono">-</td>
                              <td className="p-4 text-right font-mono text-white">{formatDuration(combinedStats.duration)}</td>
                              <td className="p-4 text-right font-mono text-white">{(combinedStats.distance * 1000).toFixed(0)} m</td>
                              <td className="p-4 text-right font-mono text-white">{combinedStats.avgSpeed.toFixed(1)} km/h</td>
                              <td className="p-4 text-right font-mono text-slate-500">-</td>
                              <td className="p-4 text-right font-mono text-slate-500">-</td>
                              <td className="p-4"></td>
                            </tr>
                          )}

                          {/* Individual Segments */}
                          {visibleSegments.map((seg) => (
                            <tr 
                              key={seg.id} 
                              onClick={() => setSelectedSegmentId(seg.id)}
                              className={`cursor-pointer transition-colors group ${
                                selectedSegmentId === seg.id 
                                  ? 'bg-cyan-950/40 border-l-4 border-cyan-500' 
                                  : 'hover:bg-slate-800 border-l-4 border-transparent'
                              }`}
                            >
                              <td className={`p-4 font-medium ${selectedSegmentId === seg.id ? 'text-white' : 'text-slate-300'}`}>
                                Run #{seg.id}
                              </td>
                              <td className="p-4 text-slate-400 font-mono text-xs">
                                 {seg.stats.startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}
                              </td>
                              <td className="p-4 text-right font-mono text-slate-200 group-hover:text-white">
                                 {formatDuration(seg.stats.duration)}
                              </td>
                              <td className="p-4 text-right font-mono text-slate-200 group-hover:text-white">
                                 {(seg.stats.distance * 1000).toFixed(0)} m
                              </td>
                              <td className="p-4 text-right font-mono">
                                 <span className={`${seg.stats.avgSpeed > 15 ? 'text-emerald-400' : 'text-slate-200'}`}>
                                   {seg.stats.avgSpeed.toFixed(1)}
                                 </span> <span className="text-xs text-slate-600">km/h</span>
                              </td>
                              <td className="p-4 text-right font-mono text-slate-400">
                                 {seg.stats.maxSpeed.toFixed(1)}
                              </td>
                              <td className="p-4 text-right font-mono">
                                 {seg.stats.avgHeartRate ? (
                                   <span className="text-red-400">{seg.stats.avgHeartRate}</span>
                                 ) : <span className="text-slate-700">-</span>}
                              </td>
                              <td className="p-4 text-right">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleHideSegment(seg.id);
                                  }}
                                  className="p-2 text-slate-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                  title="Remove Run"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                       </tbody>
                     </table>
                   </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
